import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import api from "../api";
import TechStatsBlocks from "../components/TechStatsBlocks";
import useReportTracking from "../hooks/useReportTracking";
import {
  Check, X, AlertTriangle, ChevronDown, ChevronRight, Minus,
  ArrowRight, Lock, Target, Phone, CalendarDays, Tag, TrendingUp, FileDown, BadgeCheck,
} from "lucide-react";

/**
 * Public report v2.43 — Max Valle brand (orange #c95a1a / Montserrat).
 * Single goal: book the free 30-minute consultation.
 *
 * Sections: header · verdetto · diagnosi (catena verticale + numeri chiave)
 *           · settore + top keyword · concorrenti · prova AI · domanda di
 *           mercato · problemi · CTA principale · dettagli tecnici.
 */

const CALENDAR_URL = "https://calendar.app.google/F1Qg6SoTkmh9dn7D7";
const PHONE_FALLBACK = "800 180 440";

const CREDENTIALS = [
  "Certified Professional Ethical Hacker n° 4053103",
  "International Web Association n° 0312827",
  "Membro Federprivacy n° FP-9572",
  "Associazione Informatici Professionisti n° 3241",
  "AIFIA — Associazione Italiana Formatori di Intelligenza Artificiale",
  "Consulente Tecnico d'Ufficio — Tribunale di Lodi",
];

const STATUS = {
  green:   { color: "#16a34a", bg: "#f0fdf4", border: "#bbf7d0", label: "Ok" },
  yellow:  { color: "#ca8a04", bg: "#fefce8", border: "#fef08a", label: "Da migliorare" },
  red:     { color: "#dc2626", bg: "#fef2f2", border: "#fecaca", label: "Critico" },
  blocked: { color: "#94a3b8", bg: "#f1f5f9", border: "#cbd5e1", label: "Non valutabile" },
};

const STATUS_ICON = {
  green: <Check size={22} strokeWidth={3}/>,
  yellow: <AlertTriangle size={20} strokeWidth={2.6}/>,
  red: <X size={22} strokeWidth={3}/>,
  blocked: <Minus size={22} strokeWidth={3}/>,
};

// Verdict copy keyed by (break_link, status) — alarming tone (richiesta Max).
const VERDICT_TEMPLATES = {
  "1_red": {
    title: (n) => `${n}, il tuo sito perde clienti prima ancora di mostrarli cosa offri`,
    body: "Gli errori tecnici fanno scappare chi arriva dal telefono. Finché non sistemiamo le fondamenta, ogni euro speso in pubblicità o SEO si disperde.",
  },
  "2_red": {
    title: (n) => `${n}, il tuo sito è praticamente invisibile su Google`,
    body: "La base tecnica regge, ma Google non capisce di cosa ti occupi: chi cerca i tuoi servizi oggi trova i tuoi concorrenti, non te.",
  },
  "3_red": {
    title: (n) => `${n}, Google non si fida del tuo sito`,
    body: "Il sito è ben costruito, ma per Google non sei un'autorità nel tuo settore. E se Google non si fida, nemmeno ChatGPT e le altre AI ti consigliano.",
  },
  "4_red": {
    title: (n) => `${n}, Google ti trova ma le AI non ti conoscono`,
    body: "Chi chiede un consiglio a ChatGPT o a Google AI non sente il tuo nome: è il canale che sta crescendo più in fretta e stai restando fuori.",
  },
  "1_yellow": {
    title: (n) => `${n}, il tuo sito rallenta e qualcuno se ne va`,
    body: "Nulla di irreparabile, ma ogni secondo di attesa è un potenziale cliente che torna su Google e clicca un concorrente.",
  },
  "2_yellow": {
    title: (n) => `${n}, su Google ti vede solo chi già ti conosce`,
    body: "Sei visibile per poche ricerche, quasi tutte sul tuo nome. Chi cerca il servizio e non te non ti trova.",
  },
  "3_yellow": {
    title: (n) => `${n}, i tuoi concorrenti sono più autorevoli di te per Google`,
    body: "Hai una base solida, ma l'autorevolezza del dominio è ancora bassa: ti tiene fuori dalle prime posizioni che portano chiamate.",
  },
  "4_yellow": {
    title: (n) => `${n}, le AI ti citano solo a tratti`,
    body: "Sei sulla soglia: con pochi interventi diventi la prima risposta anche per ChatGPT e Google AI Overview.",
  },
  green: {
    title: (n) => `${n}, il tuo sito è in ottima salute`,
    body: "Tutti e quattro gli anelli della catena reggono. Vediamo insieme come trasformare questo vantaggio in più richieste di preventivo.",
  },
};

const FIXED_TITLE = "Sito invisibile sui motori di ricerca e non citato dalle A.I.";

function pickVerdict(breakPoint) {
  if (!breakPoint) return VERDICT_TEMPLATES.green;
  const t = VERDICT_TEMPLATES[`${breakPoint.link}_${breakPoint.status}`]
    || VERDICT_TEMPLATES[`${breakPoint.link}_yellow`]
    || VERDICT_TEMPLATES.green;
  // v2.58 — the alarming fixed headline is used ONLY when the chain is really
  // broken (a red link). A site that is merely improvable gets the specific,
  // truthful headline: a site with 83k visits/month is not "invisibile".
  if (breakPoint.status === "red") return { title: () => FIXED_TITLE, body: t.body };
  return t;
}

function fmt(n) {
  if (n == null) return "—";
  // Italian grouping from 4 digits ("2.615"): Intl skips it for 4-digit numbers.
  return String(Math.round(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

// Tone helpers — negative data is ALWAYS red.
function tone(v, { bad, warn }) {        // higher is better
  return v < bad ? "bad" : v < warn ? "warn" : "good";
}
function toneLow(v, { warn, bad }) {     // lower is better
  return v > bad ? "bad" : v > warn ? "warn" : "good";
}
function toneBool(isBad) {
  return isBad ? "bad" : "good";
}

function titleCase(s) {
  if (!s) return "";
  return s.trim().split(/\s+/).map((w) =>
    w.length <= 3 && w === w.toUpperCase() ? w : w[0].toUpperCase() + w.slice(1).toLowerCase()
  ).join(" ");
}

// ============================================================================
export default function PublicAnalisiV2() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [showTech, setShowTech] = useState(false);
  const { track, sentinel } = useReportTracking(token);

  useEffect(() => {
    api.get(`/public/analisi/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e.response?.data?.detail || "Report non trovato."));
  }, [token]);

  if (error) return <div style={{padding: 40, fontFamily: "Montserrat, sans-serif"}}>{error}</div>;
  if (!data) return <div style={{padding: 40, textAlign: "center", fontFamily: "Montserrat, sans-serif"}}>Caricamento…</div>;

  const company = data.company || {};
  const companyName = titleCase(company.companyName || company.name || "");
  const website = (company.website || company.domain || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
  const chainData = data.visibilityChain || {};
  const chain = chainData.chain || [];
  const breakPoint = chainData.break_point;
  const detected = data.detectedProblems || [];
  const bySeverity = {
    critical: detected.filter((p) => p.severity === "critical"),
    important: detected.filter((p) => p.severity === "important"),
    minor: detected.filter((p) => p.severity === "minor"),
  };
  const top3problems = [...bySeverity.critical, ...bySeverity.important, ...bySeverity.minor].slice(0, 3);
  const competitors = data.competitors || [];
  const ai = data.aiContext || null;
  const market = data.marketDemand || null;
  const whatYouOffer = data.whatYouOffer || "";
  const whereYouWork = data.whereYouWork || "";
  const reviews = data.reviews || [];
  const verdict = pickVerdict(breakPoint);
  const lhData = data.lighthouse || {};
  const perfIssues = ((lhData.mobile || {}).opportunities || []).length
                   + ((lhData.desktop || {}).opportunities || []).length;
  const m = (data.summary || {}).metrics || {};
  const seo = data.seozoom || {};
  // v2.62 — logo e recapiti arrivano dal pannello Impostazioni
  const brand = data.branding || {};
  const PHONE = brand.phone || PHONE_FALLBACK;
  const brandSite = (brand.website || "maxvalle.it").replace(/^https?:\/\//, "");
  const sectors = (seo.sectors || [])
    .filter((s) => s && s.name)
    .slice()
    .sort((a, b) => (b.percent || 0) - (a.percent || 0) || (b.keywords || 0) - (a.keywords || 0));
  const topKeywords = (seo.top_keywords || []).filter((k) => k && k.keyword).slice(0, 5);
  const dateStr = new Date(data.completedAt || Date.now()).toLocaleDateString(
    "it-IT", {day: "numeric", month: "long", year: "numeric"}
  );
  const ctaHref = `${data.calendarUrl || CALENDAR_URL}?utm_source=report&utm_medium=web&utm_campaign=sitidaincubo&utm_content=${encodeURIComponent(token)}`;

  // v2.58 — only a red break blocks the links after it.
  const statusOf = (l) => (l.blocked ? "blocked" : l.status);
  const chainBroken = breakPoint?.status === "red";

  const th = data.chainThresholds || {};
  const numbers = [
    { key: "visits", value: fmt(m.estimated_traffic || 0), label: "visite/mese da Google",
      tone: tone(m.estimated_traffic || 0, {bad: th.trafficRed ?? 100, warn: th.trafficYellow ?? 500}),
      hint: "Quante persone arrivano sul sito ogni mese dalle ricerche, senza pubblicità." },
    { key: "keywords", value: fmt(m.total_keywords || 0), label: "parole chiave posizionate",
      tone: tone(m.total_keywords || 0, {bad: th.keywordsRed ?? 100, warn: th.keywordsYellow ?? 300}),
      hint: "Per quante ricerche il tuo sito compare oggi nei risultati di Google." },
    { key: "authority", value: `${Math.round(m.z_authority || 0)}`, suffix: "/100",
      label: "autorevolezza del dominio",
      tone: tone(m.z_authority || 0, {bad: th.authorityRed ?? 30, warn: th.authorityYellow ?? 46}),
      hint: "Quanto Google considera affidabile il tuo sito. Sotto 30 fai fatica a salire." },
    { key: "speed", value: `${m.performance_score_mobile || 0}`, suffix: "/100",
      label: "velocità da smartphone",
      tone: tone(m.performance_score_mobile || 0, {bad: th.speedRed ?? 70, warn: th.speedYellow ?? 90}),
      hint: "Più è bassa, più visitatori abbandonano prima di vedere la pagina." },
  ];
  if (detected.length > 0) {
    const sevHint = [
      bySeverity.critical.length && `${bySeverity.critical.length} critic${bySeverity.critical.length === 1 ? "o" : "i"}`,
      bySeverity.important.length && `${bySeverity.important.length} important${bySeverity.important.length === 1 ? "e" : "i"}`,
      bySeverity.minor.length && `${bySeverity.minor.length} minor${bySeverity.minor.length === 1 ? "e" : "i"}`,
      perfIssues && `${perfIssues} sulle prestazioni`,
    ].filter(Boolean).join(" · ");
    numbers.push({ key: "problems", value: fmt(detected.length),
      label: detected.length === 1 ? "problema trovato sul sito" : "problemi trovati sul sito",
      tone: "bad", hint: sevHint });
  }

  return (
    <div className="v2-shell" data-testid="report-v2">
      {/* ------------------------------------------------------- HEADER */}
      <header className="v2-header">
        <div className="v2-header-in">
          <div className="v2-brand">
            <img src={brand.logoUrl || "/maxvalle-logo@2x.png"} alt="Max Valle" className="v2-logo"/>
            <div>
              <div className="v2-company" data-testid="v2-company">{companyName}</div>
              <div className="v2-domain">{website}{website && " · "}Report del {dateStr}</div>
            </div>
          </div>
          <a href={`tel:${PHONE.replace(/\s/g, "")}`} className="v2-phone-pill" data-testid="v2-header-phone">
            <Phone size={14}/> {PHONE}
          </a>
        </div>
      </header>

      {/* ------------------------------------------------------- VERDETTO */}
      <section className="v2-hero" data-testid="verdict-hero">
        <div className="v2-badge-top">Analisi riservata · {website || "il tuo sito"}</div>
        <h1 className="v2-title" data-testid="v2-verdict-title">{verdict.title(companyName || "Ciao")}</h1>
        {sectors.length > 0 && (
          <div className="v2-title-sector" data-testid="v2-title-sector">
            Google ti posiziona come una azienda appartenente al settore <span>{sectors[0].name}</span>
          </div>
        )}
        <p className="v2-lede">{verdict.body}</p>
        <a href={ctaHref} onClick={()=>track("cta_cliccato")} target="_blank" rel="noreferrer" className="v2-cta"
           data-testid="cta-button-primary">
          Prenota la consulenza gratuita di 30 minuti <ArrowRight size={18}/>
        </a>
        <div className="v2-hero-trust" data-testid="v2-hero-trust">
          <strong>33 anni di esperienza, oltre 2.500 clienti.</strong>
          <span>Nessun impegno · Nessuna vendita telefonica · 2 libri in omaggio</span>
        </div>
      </section>

      {/* ------------------------------------------------------- DIAGNOSI */}
      <section className="v2-diag">
        <div className="v2-diag-grid">
          {/* ---- catena verticale */}
          {chain.length > 0 && (
            <div className="v2-chain-col" data-testid="visibility-chain" ref={sentinel("catena_vista")}>
              <div className="v2-eyebrow">La catena della visibilità</div>
              <h2 className="v2-h2">
                {chainBroken
                  ? "Perché oggi non arrivano richieste dal sito"
                  : breakPoint
                    ? "Dove la tua visibilità si indebolisce"
                    : "La tua catena della visibilità regge"}
              </h2>
              <p className="v2-sub">
                {chainBroken
                  ? "Quattro passaggi devono funzionare in sequenza. Il primo che si rompe blocca tutti quelli che vengono dopo."
                  : "Quattro passaggi devono funzionare in sequenza. Ecco come sta ognuno sul tuo sito oggi."}
              </p>

              <div className="v2-chain">
                {chain.map((l) => {
                  const st = statusOf(l);
                  const s = STATUS[st] || STATUS.blocked;
                  const isBreak = breakPoint?.link === l.link;
                  return (
                    <div key={l.link}
                         className={`v2-step ${isBreak ? "v2-step-break" : ""} ${isBreak && breakPoint.status === "yellow" ? "v2-step-break-warn" : ""} ${st === "blocked" ? "v2-step-off" : ""}`}
                         data-testid={`chain-node-${l.link}`}>
                      <div className="v2-step-rail">
                        <div className="v2-step-dot" style={{background: s.color, borderColor: s.border}}>
                          {STATUS_ICON[st]}
                        </div>
                        <div className="v2-step-line" style={{background: s.border}}/>
                      </div>
                      <div className="v2-step-body" style={{background: s.bg, borderColor: s.border}}>
                        {isBreak && (
                          <div className={`v2-break-badge ${breakPoint.status === "yellow" ? "v2-break-badge-warn" : ""}`}
                               data-testid="break-point-badge">
                            <AlertTriangle size={12}/>{" "}
                            {breakPoint.label || (breakPoint.status === "yellow"
                              ? "La tua catena si indebolisce qui"
                              : "La tua catena si interrompe qui")}
                          </div>
                        )}
                        <div className="v2-step-head">
                          <span className="v2-step-n">{l.link}</span>
                          <span className="v2-step-title">{l.title}</span>
                          <span className="v2-step-chip" style={{color: s.color, borderColor: s.border}}>{s.label}</span>
                        </div>
                        <div className="v2-step-meaning">{l.desc}</div>
                        {st === "blocked" ? (
                          <div className="v2-step-detail v2-step-detail-off">
                            Non valutabile: finché il passaggio {breakPoint.link}{" "}
                            {breakPoint.status === "yellow" ? "non è solido" : "è bloccato"}, questo non
                            può portarti clienti.
                          </div>
                        ) : (
                          l.detail && <div className="v2-step-detail">{l.detail}</div>
                        )}
                      </div>
                    </div>
                  );
                })}

                {/* ---- GOAL node */}
                <div className="v2-step v2-step-goal" data-testid="chain-node-goal">
                  <div className="v2-step-rail">
                    <div className="v2-step-dot v2-dot-goal"><Target size={22} strokeWidth={2.6}/></div>
                  </div>
                  <div className="v2-step-body v2-goal-body">
                    <div className="v2-goal-label">Obiettivo</div>
                    <div className="v2-goal-title">Visibilità, traffico e clienti</div>
                    <div className="v2-goal-text">
                      È quello che ottieni quando tutti e quattro i passaggi funzionano insieme.
                    </div>
                  </div>
                </div>
              </div>

              <a href={ctaHref} onClick={()=>track("cta_cliccato")} target="_blank" rel="noreferrer" className="v2-cta-inline"
                 data-testid="v2-cta-under-chain">
                {chainBroken ? "Vediamo insieme come ripararla" : "Vediamo insieme come rafforzarla"} <ArrowRight size={15}/>
              </a>
            </div>
          )}

          {/* ---- numeri chiave */}
          <aside className="v2-nums-col" data-testid="key-numbers-block">
            <div className="v2-nums-card">
              <div className="v2-eyebrow">I tuoi numeri di oggi</div>
              {numbers.map((n) => (
                <div key={n.key} className="v2-num" data-testid={`v2-num-${n.key}`}>
                  <div className={`v2-num-val v2-${n.tone || "neutral"}`}>
                    {n.value}{n.suffix && <span className="v2-num-suffix">{n.suffix}</span>}
                  </div>
                  <div className="v2-num-lbl">{n.label}</div>
                  <div className="v2-num-hint">{n.hint}</div>
                </div>
              ))}
            </div>
            <div className="v2-side-cta">
              <CalendarDays size={20}/>
              <div>
                <strong>30 minuti con Max Valle</strong>
                <p>Ti mostro tutti gli errori e il piano per risolverli, nell'ordine giusto.</p>
              </div>
              <a href={ctaHref} onClick={()=>track("cta_cliccato")} target="_blank" rel="noreferrer" className="v2-cta v2-cta-sm"
                 data-testid="v2-cta-sidebar">
                Prenota ora <ArrowRight size={16}/>
              </a>
            </div>
          </aside>
        </div>
      </section>

      {/* ------------------------------------------------- SETTORE + KEYWORD */}
      {(sectors.length > 0 || topKeywords.length > 0) && (
        <section className="v2-sector" data-testid="sector-block">
          <div className="v2-eyebrow">Come ti vede Google</div>
          {sectors.length > 0 && (
            <>
              <h2 className="v2-h2">Google vede il tuo sito in questo settore merceologico</h2>
              <div className="v2-sector-big" data-testid="v2-sector-primary">
                <Tag size={22}/> {sectors[0].name}
                {sectors[0].percent ? <span className="v2-sector-pct">{Math.round(sectors[0].percent)}% delle tue ricerche</span> : null}
              </div>
              {sectors.length > 1 && (
                <div className="v2-sector-chips">
                  {sectors.slice(1, 6).filter((s) => (s.percent || 0) >= 5).map((s) => (
                    <span key={s.name} className="v2-chip">
                      {s.name}{s.percent ? ` · ${Math.round(s.percent)}%` : ""}
                    </span>
                  ))}
                </div>
              )}
              <p className="v2-sector-note">
                Se il settore qui sopra non è quello in cui lavori davvero, è il primo problema da
                risolvere: Google ti sta mostrando alle persone sbagliate.
              </p>
            </>
          )}

          {topKeywords.length > 0 && (
            <div className="v2-kw" data-testid="top-keywords-table">
              <div className="v2-kw-title"><TrendingUp size={18}/> Le prime 5 ricerche per cui compari</div>
              <div className="v2-kw-head">
                <span>Ricerca su Google</span><span>Pos.</span><span>Ricerche/mese</span>
              </div>
              {topKeywords.map((k, i) => (
                <div key={i} className="v2-kw-row" data-testid={`v2-kw-row-${i}`}>
                  <span className="v2-kw-name">{k.keyword}</span>
                  <span className={`v2-kw-pos ${k.position > 10 ? "v2-kw-pos-bad" : ""}`}>
                    {k.position ? `${k.position}°` : "—"}
                  </span>
                  <span className="v2-kw-vol">{fmt(k.volume || 0)}</span>
                </div>
              ))}
              <div className="v2-kw-callout" data-testid="v2-kw-callout">
                <AlertTriangle size={20}/>
                <div>
                  <strong>Oltre la 10ª posizione sei in seconda pagina di Google.</strong>
                  Il 95% delle persone non ci arriva mai: per loro il tuo sito non esiste, e il
                  traffico che non arriva è il motivo per cui non ricevi richieste.
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      {/* ------------------------------------------------------- CONCORRENTI */}
      {competitors.length > 0 && (
        <section className="v2-card-sec" data-testid="competitor-comparison" ref={sentinel("concorrenti_visti")}>
          <div className="v2-eyebrow">Tu contro i tuoi concorrenti</div>
          <h2 className="v2-h2">{competitorHeadline(competitors, m.estimated_traffic || 0)}</h2>
          <CompetitorBars
            you={{domain: website, traffic: m.estimated_traffic || 0, keywords: m.total_keywords || 0}}
            competitors={competitors}
          />
        </section>
      )}

      {/* ------------------------------------------------------- PROVA AI */}
      {ai && ai.has_ai_overview && (
        <section className="v2-card-sec" data-testid="ai-chat-mock">
          <div className="v2-eyebrow">La prova AI</div>
          <h2 className="v2-h2">Cosa risponde l'AI di Google a chi cerca i tuoi servizi</h2>
          <div className="v2-chat">
            <div className="v2-chat-q">
              <strong>La domanda:</strong> "Quali sono i migliori {whatYouOffer || "professionisti"}
              {whereYouWork ? ` a ${whereYouWork}` : ""}?"
            </div>
            <div className="v2-chat-a">
              <strong>Google AI Overview cita:</strong>
              <ul>
                {(ai.tests?.[0]?.sources || []).slice(0, 5).map((s, i) => <li key={i}>{s}</li>)}
              </ul>
              {ai.cited === false && (
                <div className="v2-not-cited" data-testid="v2-ai-not-cited">
                  {companyName}: non citata
                </div>
              )}
              {ai.cited === true && <div className="v2-cited">{companyName}: citata</div>}
            </div>
          </div>
        </section>
      )}

      {/* ------------------------------------------------------- DOMANDA */}
      {market && market.monthly_searches_total > 0 && (
        <section className="v2-cost" data-testid="v2-market">
          <div className="v2-eyebrow">Cosa ti costa</div>
          <p className="v2-cost-line">
            Ogni mese circa <strong>{fmt(market.monthly_searches_total)}</strong> persone cercano{" "}
            <em>{whatYouOffer}</em>{whereYouWork ? <> a <em>{whereYouWork}</em></> : ""}.
            Oggi ne intercetti circa <strong>{fmt(m.estimated_traffic || 0)}</strong>.
            Le altre vanno ai tuoi concorrenti.
          </p>
          <div className="v2-stima">stima su dati Google</div>
        </section>
      )}

      {/* ------------------------------------------------------- PROBLEMI */}
      {detected.length > 0 && (
        <section className="v2-card-sec" data-testid="problems-catalog">
          <div className="v2-eyebrow">Problemi trovati sul tuo sito</div>
          <h2 className="v2-h2">
            {detected.length === 1
              ? "Abbiamo trovato 1 problema che ti sta costando visibilità"
              : `Abbiamo trovato ${detected.length} problemi che ti stanno costando visibilità`}
          </h2>
          <div className="v2-sev-row" data-testid="v2-severity-row">
            {bySeverity.critical.length > 0 && (
              <span className="v2-sev-pill v2-sev-pill-crit">{bySeverity.critical.length} critic{bySeverity.critical.length === 1 ? "o" : "i"}</span>
            )}
            {bySeverity.important.length > 0 && (
              <span className="v2-sev-pill v2-sev-pill-imp">{bySeverity.important.length} important{bySeverity.important.length === 1 ? "e" : "i"}</span>
            )}
            {bySeverity.minor.length > 0 && (
              <span className="v2-sev-pill v2-sev-pill-min">{bySeverity.minor.length} minor{bySeverity.minor.length === 1 ? "e" : "i"}</span>
            )}
            {perfIssues > 0 && (
              <span className="v2-sev-pill v2-sev-pill-perf">
                + {perfIssues} interventi sulle prestazioni
              </span>
            )}
          </div>
          <div className="v2-problem-cards">
            {top3problems.map((p) => {
              const c = p.severity === "critical" ? STATUS.red : p.severity === "important" ? STATUS.yellow : STATUS.blocked;
              return (
                <div key={p.key} className="v2-problem-card" style={{borderLeftColor: c.color}}
                     data-testid={`v2-problem-${p.key}`}>
                  <div className="v2-problem-title">
                    <AlertTriangle size={18} color={c.color}/>
                    <strong>{p.title_it}</strong>
                  </div>
                  <div className="v2-problem-meaning">{p.meaning_for_business}</div>
                </div>
              );
            })}
            {detected.length > 3 && (
              <div className="v2-locked-card" data-testid="v2-locked-card">
                <Lock size={22}/>
                <div>
                  <strong>Gli altri {detected.length - 3} problemi</strong>
                  <p>E l'ordine giusto in cui risolverli li vediamo insieme nella consulenza gratuita.</p>
                </div>
                <a href={ctaHref} onClick={()=>track("cta_cliccato")} target="_blank" rel="noreferrer" className="v2-cta v2-cta-sm">
                  Sbloccali <ArrowRight size={16}/>
                </a>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ------------------------------------------------------- CTA FINALE */}
      <section className="v2-cta-section" data-testid="main-cta-section">
        <div className="v2-eyebrow">Come funziona</div>
        <h2 className="v2-cta-title">
          In 30 minuti ti mostro tutti gli errori trovati sul tuo sito e il piano per risolverli,
          nell'ordine giusto.
        </h2>
        <p className="v2-cta-sub">Nessun impegno. Nessuna vendita telefonica.</p>

        <div className="v2-cta-gifts">
          <img src="/img/ebook-siti-da-incubo.webp" alt="Siti da Incubo"/>
          <img src="/img/ebook-claude.jpg" alt="Il mio collega si chiama Claude"/>
          <div className="v2-gift-text">
            <div className="v2-gift-label">In omaggio</div>
            <p>2 libri di Max Valle in versione digitale, subito dopo la call</p>
          </div>
        </div>

        <div className="v2-calendar-card">
          <div className="v2-calendar-header"><CalendarDays size={20}/> Scegli il tuo slot di 30 minuti</div>
          <div className="v2-calendar-slots">
            <div>Lun-Ven · 9:00-18:00</div><div>Anche in videochiamata</div><div>Nessun impegno</div>
          </div>
          <a href={ctaHref} onClick={()=>track("cta_cliccato")} target="_blank" rel="noreferrer" className="v2-cta"
             data-testid="v2-cta-calendar-btn">
            Apri il calendario e scegli il giorno <ArrowRight size={18}/>
          </a>
          <a href={`${process.env.REACT_APP_BACKEND_URL || ""}/api/public/analisi/${token}/pdf`}
             target="_blank" rel="noreferrer" className="v2-pdf-btn" data-testid="v2-download-pdf"
             onClick={()=>track("pdf_scaricato")}>
            <FileDown size={17}/> Scarica l'analisi completa in PDF
          </a>
          <div className="v2-cta-alt">
            Preferisci telefonare? <a href={`tel:${PHONE.replace(/\s/g, "")}`}>{PHONE}</a>
          </div>
        </div>

        <div className="v2-creds" data-testid="v2-credentials">
          <div className="v2-creds-head">33 anni di esperienza, oltre 2.500 clienti.</div>
          <div className="v2-creds-sub">Dal 1993, in 12 paesi.</div>
          <div className="v2-creds-title">Certificazioni e albi</div>
          <ul className="v2-creds-list">
            {CREDENTIALS.map((c) => (
              <li key={c}><BadgeCheck size={15}/> {c}</li>
            ))}
          </ul>
        </div>

        {reviews.length > 0 && (
          <div className="v2-reviews" data-testid="v2-reviews">
            {reviews.slice(0, 3).map((r, i) => (
              <blockquote key={i}>"{r.text}"<cite>— {r.author}</cite></blockquote>
            ))}
          </div>
        )}
      </section>

      {/* ------------------------------------------------------- TECNICO */}
      <section className="v2-tech" data-testid="technical-accordion">
        <div className="v2-eyebrow">Analisi tecnica</div>
        <h2 className="v2-h2">Tutti i dettagli tecnici rilevati</h2>
        <p className="v2-sub">
          PageSpeed, Core Web Vitals, controlli on-page, backlink, keyword e settori:
          ogni singolo dato misurato sul tuo sito, senza filtri.
        </p>
        <button onClick={() => setShowTech(!showTech)} className="v2-tech-link" data-testid="v2-tech-toggle">
          {showTech ? <ChevronDown size={18}/> : <ChevronRight size={18}/>}
          {showTech
            ? "Chiudi i dettagli tecnici"
            : "Clicca per vedere tutti i dettagli rilevati del tuo sito"}
        </button>
        {showTech && (
          <TechDetails
            metrics={m}
            lhMobile={(data.lighthouse || {}).mobile || {}}
            lhDesktop={(data.lighthouse || {}).desktop || {}}
            rank={(data.dataforseo || {}).rank || {}}
            backlinks={(data.dataforseo || {}).backlinks || {}}
            onPage={data.onPageData || {}}
            seoMetrics={seo.metrics || {}}
            bestPages={seo.best_pages || []}
            keywords={seo.top_keywords || []}
            sectors={sectors}
            robots={data.robotsScan || {}}
            jsonld={data.jsonldScan || {}}
            aiReadiness={data.aiReadiness || {}}
            detected={detected}
          />
        )}
      </section>

      <a href={ctaHref} onClick={()=>track("cta_cliccato")} target="_blank" rel="noreferrer" className="v2-sticky-cta" data-testid="mobile-sticky-cta">
        <span>Consulenza 30 min <em>gratuita</em></span>
        <span className="v2-sticky-btn">Prenota ora</span>
      </a>

      <footer className="v2-footer" data-testid="v2-footer">
        {brand.companyLine || "Max Valle · It's Genius Srl"} · P.IVA 08770720962 ·
        Numero Verde {PHONE} ·{" "}
        <a href={`https://${brandSite}`} target="_blank" rel="noreferrer">{brandSite}</a>
        {brand.privacyUrl && (
          <> · <a href={brand.privacyUrl} target="_blank" rel="noreferrer"
                  data-testid="v2-footer-privacy">Privacy Policy</a></>
        )}
      </footer>

      <ReportV2Styles />
    </div>
  );
}

// ---------- competitor bars -------------------------------------------------
function competitorHeadline(competitors, yourTraffic) {
  const top = [...competitors].sort(
    (a, b) => (b.estimated_traffic || 0) - (a.estimated_traffic || 0))[0];
  if (!top || !top.estimated_traffic) return "Ecco chi ti supera su Google";
  if (!yourTraffic) {
    return `${top.domain} riceve ${fmt(top.estimated_traffic)} visite al mese da Google. Tu nessuna.`;
  }
  const ratio = top.estimated_traffic / yourTraffic;
  // v2.60 — ratio < 1.2 can also mean WE are ahead: never claim "same visits"
  // when we get several times more than the strongest competitor.
  if (yourTraffic >= top.estimated_traffic * 1.5) {
    return `Oggi sei davanti: ${top.domain} riceve ${fmt(top.estimated_traffic)} visite al mese, tu ${fmt(yourTraffic)}`;
  }
  if (ratio < 1.2) return `Tu e ${top.domain} ricevete più o meno le stesse visite da Google`;
  if (ratio >= 30) {
    return `${top.domain} riceve ${fmt(top.estimated_traffic)} visite al mese da Google, tu ${fmt(yourTraffic)}`;
  }
  const n = ratio >= 10 ? Math.round(ratio) : Math.round(ratio * 10) / 10;
  const label = String(n).replace(".", ",");
  return `${top.domain} riceve ${label} volte le tue visite da Google`;
}

function CompetitorBars({ you, competitors }) {
  const rows = [
    { label: "Visite al mese da Google", key: "estimated_traffic", you: you.traffic },
    { label: "Parole chiave posizionate", key: "total_keywords", you: you.keywords },
  ];
  return (
    <div className="v2-bars">
      {rows.map((r) => {
        const comps = competitors.slice(0, 3).map((c) => ({domain: c.domain, val: c[r.key] || 0}));
        const max = Math.max(r.you, ...comps.map((c) => c.val), 1);
        // v2.60 — our bar is red only when we are actually behind: leading and
        // being painted red made the report look wrong.
        const ahead = r.you >= Math.max(...comps.map((c) => c.val), 0);
        const all = [{domain: "Il tuo sito", val: r.you, mine: true, ahead}, ...comps];
        return (
          <div key={r.label} className="v2-bar-row">
            <div className="v2-bar-label">{r.label}</div>
            {all.map((c) => (
              <div key={c.domain} className={`v2-bar-line ${c.mine ? (c.ahead ? "v2-bar-mine v2-bar-ahead" : "v2-bar-mine") : ""}`}>
                <div className="v2-bar-name">{c.domain}</div>
                <div className="v2-bar-track">
                  <div className="v2-bar-fill" style={{width: `${Math.max((c.val / max) * 100, 1.5)}%`}}/>
                </div>
                <div className="v2-bar-val">{fmt(c.val)}</div>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
}

// ---------- technical details (everything we measured) ----------------------
const CWV_ROWS = [
  ["LCP — caricamento del contenuto principale", "lcp", "s", 2.5, 4],
  ["FCP — primo contenuto visibile", "fcp", "s", 1.8, 3],
  ["CLS — stabilità visiva della pagina", "cls", "", 0.1, 0.25],
  ["TTFB — risposta del server", "ttfb", "s", 0.8, 1.8],
  ["TBT — tempo di blocco totale", "tbt", "ms", 200, 600],
  ["Speed Index", "speed_index", "s", 3.4, 5.8],
  ["TTI — tempo di interattività", "tti", "s", 3.8, 7.3],
];

const SEO_TONES = {
  stability: {bad: 30, warn: 50},
  opportunity: {bad: 20, warn: 50},
}

function seoTone(k, v) {
  const t = SEO_TONES[k];
  return t && typeof v === "number" ? tone(v, t) : undefined;
}

const LH_CATS = [
  ["Performance", "performance"],
  ["Accessibilità", "accessibility"],
  ["Best practices", "best_practices"],
  ["SEO tecnica", "seo"],
];

const ONPAGE_LABELS = {
  no_description: "Pagine senza meta description",
  no_h1_tag: "Pagine senza titolo H1",
  title_too_long: "Title troppo lunghi",
  duplicate_title: "Title duplicati",
  duplicate_description: "Meta description duplicate",
  broken_links: "Link rotti",
  broken_resources: "Risorse rotte (immagini, CSS, JS)",
  http_4xx: "Pagine in errore 4xx",
  http_5xx: "Pagine in errore 5xx",
  redirect_chain: "Catene di redirect",
  no_image_alt: "Immagini senza testo alternativo",
  low_content_rate: "Pagine con poco testo",
  no_canonical: "Pagine senza tag canonical",
  no_sitemap: "Sitemap mancante",
  no_robots_txt: "robots.txt mancante",
  no_https: "Pagine non in HTTPS",
  pages_scanned: "Pagine scansionate",
};

// Only metrics that are NOT already shown elsewhere in the report (no duplicates).
const SEO_LABELS = {
  domain_used: "Dominio analizzato",
  stability: "Stabilità del posizionamento",
  opportunity: "Opportunità di crescita",
  cpc_value: "CPC medio del settore",
  traffic_value: "Valore del traffico (€/mese equivalenti ADS)",
}

const prettyKey = (k) => (ONPAGE_LABELS[k] || SEO_LABELS[k] || k.replace(/_/g, " "));

function TechRow({ label, value, tone: t }) {
  return (
    <div className="v2-trow">
      <span className="v2-trow-l">{label}</span>
      <span className={`v2-trow-v ${t ? `v2-${t}` : ""}`}>{value}</span>
    </div>
  );
}

function TechDetails({ metrics, lhMobile, lhDesktop, rank, backlinks, onPage, seoMetrics,
                       bestPages, keywords, sectors, robots, jsonld, aiReadiness, detected }) {
  // v2.58 — only real problems: the raw DataForSEO dump also contains
  // positives (is https, canonical, seo friendly url) and the unreliable
  // text/HTML ratio flag.
  const checks = Object.entries(onPage.raw_checks || {})
    .filter(([k, v]) => ONPAGE_LABELS[k] && k !== "low_content_rate" && v > 0)
    .sort((a, b) => b[1] - a[1]);
  const thinPages = onPage.min_word_count != null ? (onPage.low_content_rate || 0) : 0;
  const opps = [
    ...(lhMobile.opportunities || []).map((o) => ({...o, dev: "Mobile"})),
    ...(lhDesktop.opportunities || []).map((o) => ({...o, dev: "Desktop"})),
  ];
  // v2.58 — a metric the data source doesn't provide must not be shown as a
  // red "0" (stability/opportunity/CPC are missing on some sources).
  const seoEntries = Object.entries(seoMetrics)
    .filter(([k, v]) => SEO_LABELS[k] && (typeof v === "number" || typeof v === "string"))
    .filter(([, v]) => !(typeof v === "number" && v === 0));

  return (
    <div className="v2-tech-body" data-testid="v2-tech-body">
      <TechStatsBlocks m={metrics} lhm={lhMobile} lhd={lhDesktop}/>

      <div className="v2-tblock" style={{marginTop: 26}}>
        <h3>Punteggi di velocità e qualità tecnica, valore per valore</h3>
        <div className="v2-tgrid2">
          {LH_CATS.map(([label, key]) => {
            const worst = Math.min(lhMobile[key] ?? 100, lhDesktop[key] ?? 100);
            return (
              <TechRow key={key} label={label} tone={tone(worst, {bad: 50, warn: 90})}
                       value={`Mobile ${lhMobile[key] ?? "—"}/100 · Desktop ${lhDesktop[key] ?? "—"}/100`}/>
            );
          })}
        </div>
      </div>

      <div className="v2-tblock">
        <h3>Core Web Vitals e tempi di caricamento</h3>
        <div className="v2-tgrid2">
          {CWV_ROWS.map(([label, key, unit, warn, bad]) => {
            const worst = Math.max(lhMobile[key] ?? 0, lhDesktop[key] ?? 0);
            return (
              <TechRow key={key} label={label} tone={toneLow(worst, {warn, bad})}
                       value={`Mobile ${lhMobile[key] ?? "—"}${unit} · Desktop ${lhDesktop[key] ?? "—"}${unit}`}/>
            );
          })}
        </div>
      </div>

      {opps.length > 0 && (
        <div className="v2-tblock" data-testid="v2-tech-opportunities">
          <h3>Interventi rilevati sulle prestazioni ({opps.length})</h3>
          <ul className="v2-tlist">
            {opps.map((o, i) => (
              <li key={`${o.dev}-${o.id}-${i}`}>
                <span className="v2-tpill">{o.dev}</span>
                <strong className="v2-bad">{o.title}</strong>
                {o.display_value ? ` — ${o.display_value}` : ""}
                {o.savings_ms ? ` · risparmio stimato ${o.savings_ms} ms` : ""}
                {o.savings_kb ? ` · ${o.savings_kb} KB` : ""}
                <div className="v2-tdesc">{o.description}</div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {(checks.length > 0 || thinPages > 0 || onPage.pages_scanned != null) && (
        <div className="v2-tblock" data-testid="v2-tech-onpage">
          <h3>Controlli on-page (scansione del tuo sito)</h3>
          <div className="v2-tgrid2">
            {onPage.pages_scanned != null && (
              <TechRow label="Pagine scansionate" value={onPage.pages_scanned}/>
            )}
            {thinPages > 0 && (
              <TechRow label={ONPAGE_LABELS.low_content_rate} value={thinPages} tone="bad"/>
            )}
            {checks.map(([k, v]) => (
              <TechRow key={k} label={prettyKey(k)} value={v} tone={toneBool(v > 0)}/>
            ))}
          </div>
        </div>
      )}

      {seoEntries.length > 0 && (
        <div className="v2-tblock" data-testid="v2-tech-seo">
          <h3>Metriche SEO e autorevolezza</h3>
          <div className="v2-tgrid2">
            {seoEntries.map(([k, v]) => (
              <TechRow key={k} label={prettyKey(k)} tone={seoTone(k, v)}
                       value={typeof v === "number" ? fmt(Math.round(v * 100) / 100) : v}/>
            ))}
          </div>
        </div>
      )}

      {(Object.keys(rank).length > 0 || Object.keys(backlinks).length > 0) && (
        <div className="v2-tblock" data-testid="v2-tech-rank">
          <h3>Posizionamento su Google e backlink</h3>
          <div className="v2-tgrid2">
            {rank.pos_1 != null && <TechRow label="Keyword in posizione 1" value={fmt(rank.pos_1)} tone={toneBool(!rank.pos_1)}/>}
            {rank.pos_2_3 != null && <TechRow label="Keyword in posizione 2-3" value={fmt(rank.pos_2_3)} tone={toneBool(!rank.pos_2_3)}/>}
            {rank.pos_4_10 != null && <TechRow label="Keyword in posizione 4-10" value={fmt(rank.pos_4_10)}/>}
            {rank.pos_11_20 != null && <TechRow label="Keyword oltre la prima pagina (11-20)" value={fmt(rank.pos_11_20)} tone={toneBool(rank.pos_11_20 > 0)}/>}
            {rank.etv != null && <TechRow label="Traffico organico stimato" value={fmt(Math.round(rank.etv))} tone={tone(rank.etv, {bad: 100, warn: 500})}/>}
            {rank.count != null && <TechRow label="Totale risultati in SERP" value={fmt(rank.count)}/>}
            {backlinks.backlinks != null && <TechRow label="Backlink totali" value={fmt(backlinks.backlinks)} tone={tone(backlinks.backlinks, {bad: 50, warn: 300})}/>}
            {backlinks.referring_domains != null && <TechRow label="Domini che ti linkano" value={fmt(backlinks.referring_domains)} tone={tone(backlinks.referring_domains, {bad: 20, warn: 80})}/>}
            {backlinks.referring_main_domains != null && <TechRow label="Domini principali che ti linkano" value={fmt(backlinks.referring_main_domains)}/>}
            {backlinks.broken_backlinks != null && <TechRow label="Backlink rotti" value={fmt(backlinks.broken_backlinks)} tone={toneBool(backlinks.broken_backlinks > 0)}/>}
            {backlinks.broken_pages != null && <TechRow label="Pagine rotte linkate" value={fmt(backlinks.broken_pages)} tone={toneBool(backlinks.broken_pages > 0)}/>}
          </div>
        </div>
      )}

      <div className="v2-tblock" data-testid="v2-tech-ai">
        <h3>Indicizzazione, dati strutturati e AI</h3>
        <div className="v2-tgrid2">
          <TechRow label="File llms.txt presente" value={aiReadiness.has_llms_txt ? "Sì" : "No"}
                   tone={toneBool(!aiReadiness.has_llms_txt)}/>
          <TechRow label="Blocca GPTBot (ChatGPT)" value={robots.blocks_gptbot ? "Sì" : "No"}
                   tone={toneBool(!!robots.blocks_gptbot)}/>
          <TechRow label="Blocca ClaudeBot (Claude)" value={robots.blocks_claudebot ? "Sì" : "No"}
                   tone={toneBool(!!robots.blocks_claudebot)}/>
          <TechRow label="Blocca PerplexityBot" value={robots.blocks_perplexitybot ? "Sì" : "No"}
                   tone={toneBool(!!robots.blocks_perplexitybot)}/>
          <TechRow label="Blocca Google-Extended" value={robots.blocks_google_extended ? "Sì" : "No"}
                   tone={toneBool(!!robots.blocks_google_extended)}/>
          <TechRow label="Schema Organization" value={jsonld.has_organization ? "Presente" : "Assente"}
                   tone={toneBool(!jsonld.has_organization)}/>
          <TechRow label="Schema LocalBusiness" value={jsonld.has_local_business ? "Presente" : "Assente"}
                   tone={toneBool(!jsonld.has_local_business)}/>
          {jsonld.types_found?.length > 0 && (
            <TechRow label="Tipi JSON-LD trovati" value={jsonld.types_found.join(", ")}/>
          )}
        </div>
      </div>

      {keywords.length > 0 && (
        <div className="v2-tblock" data-testid="v2-tech-keywords">
          <h3>Tutte le keyword rilevate ({keywords.length})</h3>
          <div className="v2-ttable-wrap">
            <table className="v2-ttable">
              <thead>
                <tr><th>Keyword</th><th>Pos.</th><th>Volume</th><th>Traffico</th><th>Intento</th><th>KD</th></tr>
              </thead>
              <tbody>
                {keywords.map((k, i) => (
                  <tr key={i}>
                    <td>{k.keyword}</td>
                    <td className={k.position > 10 ? "v2-bad" : k.position > 3 ? "v2-warn" : "v2-good"}>
                      {k.position || "—"}
                    </td>
                    <td>{fmt(k.volume || 0)}</td>
                    <td>{fmt(k.traffic || 0)}</td><td>{k.intent || "—"}</td><td>{k.kd || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {bestPages.length > 0 && (
        <div className="v2-tblock" data-testid="v2-tech-pages">
          <h3>Pagine che portano più traffico</h3>
          <div className="v2-ttable-wrap">
            <table className="v2-ttable">
              <thead><tr><th>URL</th><th>Keyword</th><th>Autorevolezza pagina</th></tr></thead>
              <tbody>
                {bestPages.map((p, i) => (
                  <tr key={i}>
                    <td className="v2-turl">{p.url}</td>
                    <td>{fmt(p.keywords || 0)}</td><td>{Math.round(p.pza || 0)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {detected.length > 0 && (
        <div className="v2-tblock" data-testid="v2-tech-problems">
          <h3>Elenco completo problemi rilevati ({detected.length})</h3>
          <ul className="v2-tlist">
            {detected.map((p) => (
              <li key={p.key}>
                <span className={`v2-sev-badge v2-sev-${p.severity}`}>{p.severity}</span>
                <strong>{p.title_it}</strong> <code>{p.key}</code>
                <div className="v2-tdesc">{p.meaning_for_business || p.explanation}</div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ---------- Styles ----------------------------------------------------------
function ReportV2Styles() {
  return (
    <style>{`
      .v2-shell {
        --mv: #c95a1a; --mv-dark: #b14c13; --mv-bg: #fdf6f0; --mv-bd: #f0d2bd;
        --ink: #0f172a; --ink2: #475569; --ink3: #64748b; --bd: #e2e8f0;
        background: #f8fafc; color: var(--ink);
        font-family: 'Montserrat', -apple-system, sans-serif;
        min-height: 100vh; padding-bottom: 110px; overflow-x: hidden;
      }
      .v2-shell * { box-sizing: border-box; }
      .v2-shell h1, .v2-shell h2 { letter-spacing: -0.02em; }

      /* ---------- header */
      .v2-header { background: #fff; border-bottom: 1px solid var(--bd); }
      .v2-header-in {
        max-width: 1120px; margin: 0 auto; padding: 14px 20px;
        display: flex; align-items: center; justify-content: space-between; gap: 16px;
      }
      .v2-brand { display: flex; align-items: center; gap: 14px; min-width: 0; }
      .v2-logo { height: 72px; width: auto; flex-shrink: 0; }
      .v2-company { font-weight: 800; font-size: 15px; line-height: 1.2; }
      .v2-domain { color: var(--ink3); font-size: 12px; margin-top: 2px; }
      .v2-phone-pill {
        display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0;
        border: 1px solid var(--mv-bd); background: var(--mv-bg); color: var(--mv) !important;
        font-weight: 700; font-size: 13px; padding: 7px 14px; border-radius: 100px;
        text-decoration: none; transition: background-color .2s, color .2s;
      }
      .v2-phone-pill:hover { background: var(--mv); color: #fff !important; }

      /* ---------- hero */
      .v2-hero {
        max-width: 1120px; margin: 0 auto; padding: 48px 20px 36px; text-align: left;
      }
      .v2-title { max-width: 820px; }
      .v2-title-sector {
        font-size: 20px; font-weight: 800; margin: 0 0 14px; max-width: 760px; line-height: 1.35;
      }
      .v2-title-sector span {
        color: var(--mv); border-bottom: 2px solid var(--mv-bd); padding-bottom: 1px;
      }
      .v2-badge-top {
        display: inline-block; font-size: 10.5px; font-weight: 800; letter-spacing: .12em;
        text-transform: uppercase; color: var(--ink3); background: #fff;
        border: 1px solid var(--bd); padding: 6px 12px; border-radius: 100px; margin-bottom: 20px;
      }
      .v2-title { font-size: 40px; line-height: 1.12; font-weight: 800; margin: 0 0 14px; }
      .v2-lede { font-size: 17px; line-height: 1.6; color: var(--ink2); margin: 0 0 26px; max-width: 640px; }
      .v2-cta {
        display: inline-flex; align-items: center; gap: 9px; background: var(--mv);
        color: #fff !important; font-weight: 700; padding: 15px 26px; border-radius: 100px;
        text-decoration: none; font-size: 16px; border: none; cursor: pointer;
        box-shadow: 0 8px 20px rgba(201,90,26,.28);
        transition: background-color .2s, transform .2s, box-shadow .2s;
      }
      .v2-cta:hover { background: var(--mv-dark); transform: translateY(-2px); box-shadow: 0 12px 26px rgba(201,90,26,.34); }
      .v2-cta-sm { padding: 11px 18px; font-size: 14px; box-shadow: none; }
      .v2-hero-trust { margin-top: 18px; font-size: 13px; color: var(--ink3); }
      .v2-hero-trust strong { display: block; font-size: 15px; color: var(--ink); margin-bottom: 3px; }

      .v2-eyebrow {
        text-transform: uppercase; letter-spacing: .13em; color: var(--ink3);
        font-size: 11px; font-weight: 800; margin-bottom: 12px;
      }
      .v2-h2 { font-size: 26px; font-weight: 800; margin: 0 0 10px; line-height: 1.25; }
      .v2-sub { color: var(--ink2); font-size: 15px; margin: 0 0 26px; max-width: 560px; line-height: 1.55; }

      /* ---------- diagnosi (chain + numbers) */
      .v2-diag { max-width: 1120px; margin: 0 auto; padding: 8px 20px 20px; }
      .v2-diag-grid {
        display: grid; grid-template-columns: minmax(0, 1fr) 330px;
        gap: 32px; align-items: start;
      }
      .v2-chain { display: flex; flex-direction: column; }
      .v2-step { display: grid; grid-template-columns: 48px minmax(0, 1fr); gap: 16px; }
      .v2-step-rail { display: flex; flex-direction: column; align-items: center; }
      .v2-step-dot {
        width: 46px; height: 46px; border-radius: 50%; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        color: #fff; border: 4px solid transparent; background-clip: padding-box;
      }
      .v2-step-line { width: 3px; flex: 1; min-height: 18px; border-radius: 3px; }
      .v2-step-body {
        border: 1px solid var(--bd); border-radius: 14px; padding: 16px 18px;
        margin-bottom: 14px; position: relative; min-width: 0;
      }
      .v2-step-head { display: flex; align-items: center; gap: 10px; flex-wrap: wrap; }
      .v2-step-n {
        font-size: 11px; font-weight: 800; color: var(--ink3);
        border: 1px solid var(--bd); background: #fff;
        width: 20px; height: 20px; border-radius: 50%;
        display: inline-flex; align-items: center; justify-content: center;
      }
      .v2-step-title { font-weight: 800; font-size: 16px; }
      .v2-step-chip {
        font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .07em;
        border: 1px solid; border-radius: 100px; padding: 3px 9px; background: #fff;
      }
      .v2-step-meaning { color: var(--ink); font-size: 14.5px; margin-top: 8px; line-height: 1.5; }
      .v2-step-detail {
        margin-top: 10px; font-size: 13px; font-weight: 700; color: var(--ink2);
        background: rgba(255,255,255,.75); border-radius: 8px; padding: 8px 11px;
        display: inline-block;
      }
      .v2-step-detail-off { font-weight: 500; font-style: italic; }
      .v2-step-off .v2-step-title, .v2-step-off .v2-step-meaning { color: #64748b; }
      .v2-step-break .v2-step-body {
        box-shadow: 0 0 0 2px rgba(220,38,38,.22), 0 10px 26px rgba(220,38,38,.12);
        padding-top: 26px;
      }
      .v2-step-break-warn .v2-step-body {
        box-shadow: 0 0 0 2px rgba(217,119,6,.25), 0 10px 26px rgba(217,119,6,.12);
      }
      .v2-break-badge {
        position: absolute; top: -11px; left: 14px;
        display: inline-flex; align-items: center; gap: 5px;
        background: #dc2626; color: #fff; font-size: 10px; font-weight: 800;
        padding: 5px 11px; border-radius: 100px; text-transform: uppercase;
        letter-spacing: .05em; box-shadow: 0 4px 10px rgba(220,38,38,.3);
      }
      .v2-break-badge-warn {
        background: #d97706; box-shadow: 0 4px 10px rgba(217,119,6,.3);
      }
      .v2-dot-goal { background: #16a34a; box-shadow: 0 0 0 6px rgba(22,163,74,.16); }
      .v2-goal-body { background: #f0fdf4; border: 2px solid #86efac; }
      .v2-goal-label {
        font-size: 10px; font-weight: 800; letter-spacing: .14em; text-transform: uppercase;
        color: #15803d;
      }
      .v2-goal-title { font-size: 20px; font-weight: 800; margin-top: 4px; }
      .v2-goal-text { font-size: 14px; color: var(--ink2); margin-top: 6px; line-height: 1.5; }
      .v2-cta-inline {
        display: inline-flex; align-items: center; gap: 7px; color: var(--mv);
        font-weight: 800; text-decoration: none; margin-top: 8px; font-size: 15px;
      }
      .v2-cta-inline:hover { text-decoration: underline; }

      .v2-nums-col { position: sticky; top: 20px; display: flex; flex-direction: column; gap: 14px; }
      .v2-nums-card {
        background: #fff; border: 1px solid var(--bd); border-radius: 16px; padding: 20px;
        box-shadow: 0 2px 10px rgba(15,23,42,.04);
      }
      .v2-num { padding: 18px 0; border-top: 1px solid #f1f5f9; }
      .v2-num:first-of-type { border-top: none; padding-top: 4px; }
      .v2-num-val { font-size: 30px; font-weight: 800; line-height: 1; }
      .v2-neutral { color: var(--ink); }
      .v2-bad { color: #dc2626 !important; }
      .v2-warn { color: #334155 !important; }
      .v2-good { color: #15803d !important; }
      .v2-num-suffix { font-size: 15px; color: var(--ink3); font-weight: 700; }
      .v2-num-lbl { font-size: 13.5px; font-weight: 700; margin-top: 4px; }
      .v2-num-hint { font-size: 12px; color: var(--ink3); margin-top: 3px; line-height: 1.45; }
      .v2-side-cta {
        background: #fff; border: 1px solid var(--mv-bd); border-radius: 16px; padding: 18px;
        display: flex; flex-direction: column; gap: 10px; align-items: flex-start; color: var(--mv);
      }
      .v2-side-cta strong { font-size: 15px; color: var(--ink); display: block; }
      .v2-side-cta p { font-size: 13px; color: var(--ink2); margin: 5px 0 0; line-height: 1.45; }

      /* ---------- settore + keywords */
      .v2-sector {
        max-width: 1120px; margin: 24px auto; padding: 30px 24px; background: #fff;
        border: 1px solid var(--bd); border-radius: 18px;
      }
      .v2-sector-big {
        display: inline-flex; align-items: center; gap: 10px; flex-wrap: wrap;
        font-size: 26px; font-weight: 800; color: var(--ink);
        background: #f8fafc; border: 1px solid var(--bd); border-left: 4px solid var(--mv);
        padding: 16px 22px; border-radius: 12px; margin: 8px 0 18px; max-width: 100%;
      }
      .v2-sector-pct { font-size: 13px; font-weight: 700; color: var(--ink2); }
      .v2-sector-chips { display: flex; flex-wrap: wrap; gap: 8px; margin-bottom: 14px; }
      .v2-chip {
        font-size: 12px; font-weight: 600; color: var(--ink2); background: #f1f5f9;
        border: 1px solid var(--bd); border-radius: 100px; padding: 5px 11px;
      }
      .v2-sector-note { font-size: 14px; color: var(--ink2); line-height: 1.55; margin: 0 0 8px; max-width: 700px; }

      .v2-kw { margin-top: 26px; border-top: 1px solid var(--bd); padding-top: 22px; }
      .v2-kw-title { display: flex; align-items: center; gap: 8px; font-weight: 800; font-size: 17px; margin-bottom: 18px; }
      .v2-kw-head, .v2-kw-row {
        display: grid; grid-template-columns: minmax(0,1fr) 92px 120px; gap: 10px;
        align-items: center; padding: 11px 0; border-bottom: 1px solid #f1f5f9;
      }
      .v2-kw-head {
        font-size: 10.5px; text-transform: uppercase; letter-spacing: .09em;
        color: var(--ink3); font-weight: 800; padding-bottom: 8px;
      }
      .v2-kw-head span:not(:first-child), .v2-kw-row span:not(:first-child) { text-align: right; }
      .v2-kw-name { font-weight: 700; font-size: 14.5px; overflow-wrap: anywhere; }
      .v2-kw-pos { font-weight: 800; color: #16a34a; font-size: 15px; }
      .v2-kw-pos-bad { color: #dc2626; }
      .v2-kw-vol { color: var(--ink2); font-size: 14px; }
      .v2-kw-foot { font-size: 12.5px; color: var(--ink3); margin-top: 12px; }

      /* ---------- generic card sections */
      .v2-card-sec {
        max-width: 1120px; margin: 32px auto; padding: 40px 34px; background: #fff;
        border: 1px solid var(--bd); border-radius: 18px;
      }
      .v2-bar-row { margin-bottom: 34px; }
      .v2-bar-label { font-weight: 800; margin-bottom: 14px; font-size: 15px; }
      .v2-bar-line {
        display: grid; grid-template-columns: 180px minmax(0, 1fr) 96px;
        gap: 14px; align-items: center; margin-bottom: 10px;
      }
      .v2-bar-name {
        font-size: 13.5px; color: var(--ink2); overflow: hidden;
        text-overflow: ellipsis; white-space: nowrap;
      }
      .v2-bar-track { height: 22px; background: #f1f5f9; border-radius: 6px; overflow: hidden; }
      .v2-bar-fill { height: 100%; border-radius: 6px; background: #94a3b8; }
      .v2-bar-val { font-weight: 800; font-size: 14px; text-align: right; color: var(--ink); }
      .v2-bar-mine .v2-bar-name { color: #b91c1c; font-weight: 800; }
      .v2-bar-mine .v2-bar-fill { background: #dc2626; }
      .v2-bar-mine .v2-bar-val { color: #b91c1c; }
      .v2-bar-ahead .v2-bar-name { color: #15803d; }
      .v2-bar-ahead .v2-bar-fill { background: #16a34a; }
      .v2-bar-ahead .v2-bar-val { color: #15803d; }

      .v2-kw-callout {
        display: flex; gap: 14px; align-items: flex-start; margin-top: 20px;
        background: #fef2f2; border: 1px solid #fecaca; border-left: 4px solid #dc2626;
        border-radius: 12px; padding: 16px 18px; font-size: 14.5px; line-height: 1.55;
        color: var(--ink);
      }
      .v2-kw-callout svg { color: #dc2626; flex-shrink: 0; margin-top: 1px; }
      .v2-kw-callout strong { display: block; color: #b91c1c; margin-bottom: 3px; font-size: 15.5px; }

      .v2-sev-row { display: flex; flex-wrap: wrap; gap: 8px; margin: 4px 0 22px; }
      .v2-sev-pill {
        font-size: 12.5px; font-weight: 800; padding: 5px 12px; border-radius: 100px;
        border: 1px solid;
      }
      .v2-sev-pill-crit { background: #fef2f2; color: #b91c1c; border-color: #fecaca; }
      .v2-sev-pill-imp { background: #fff7ed; color: #9a3412; border-color: #fed7aa; }
      .v2-sev-pill-min { background: #f1f5f9; color: #475569; border-color: #e2e8f0; }
      .v2-sev-pill-perf { background: #f8fafc; color: #334155; border-color: #cbd5e1; }

      .v2-chat { background: #f8fafc; border: 1px solid var(--bd); border-radius: 14px; padding: 18px; }
      .v2-chat-q { margin-bottom: 14px; padding-bottom: 14px; border-bottom: 1px dashed var(--bd); font-size: 15px; }
      .v2-chat-a { font-size: 15px; }
      .v2-chat-a ul { margin: 8px 0 0 20px; }
      .v2-chat-a li { color: var(--ink2); font-size: 14px; }
      .v2-not-cited {
        margin-top: 12px; padding: 10px 14px; background: #fef2f2; color: #b91c1c;
        border: 1px solid #fecaca; border-radius: 8px; font-weight: 800;
      }
      .v2-cited {
        margin-top: 12px; padding: 10px 14px; background: #f0fdf4; color: #15803d;
        border: 1px solid #bbf7d0; border-radius: 8px; font-weight: 800;
      }

      .v2-cost {
        max-width: 1120px; margin: 24px auto; padding: 32px 24px; background: var(--mv-bg);
        border: 1px solid var(--mv-bd); border-radius: 18px;
      }
      .v2-cost-line { font-size: 19px; line-height: 1.55; margin: 0; max-width: 760px; }
      .v2-stima {
        display: inline-block; font-size: 10.5px; text-transform: uppercase;
        color: var(--ink3); margin-top: 12px; letter-spacing: .1em; font-weight: 700;
      }

      .v2-problem-cards { display: flex; flex-direction: column; gap: 12px; margin-top: 18px; }
      .v2-problem-card {
        background: #f8fafc; border: 1px solid var(--bd); border-left: 5px solid;
        border-radius: 12px; padding: 15px 18px;
      }
      .v2-problem-title { display: flex; align-items: center; gap: 9px; font-size: 15px; }
      .v2-problem-meaning { color: var(--ink2); font-size: 14px; margin-top: 6px; line-height: 1.5; }
      .v2-locked-card {
        display: flex; align-items: center; gap: 16px; flex-wrap: wrap;
        background: #f1f5f9; border: 1px dashed #cbd5e1; border-radius: 12px;
        padding: 18px; color: var(--ink3);
      }
      .v2-locked-card > div { flex: 1 1 240px; }
      .v2-locked-card strong { color: var(--ink); font-size: 15px; }
      .v2-locked-card p { margin: 4px 0 0; font-size: 13.5px; }

      /* ---------- CTA finale (light, leggibile) */
      .v2-cta-section {
        max-width: 1120px; margin: 32px auto; padding: 44px 34px;
        background: linear-gradient(180deg, #ffffff 0%, #f3fdf7 100%);
        border: 1px solid #bbf7d0; border-radius: 22px;
        box-shadow: 0 10px 30px rgba(22,101,52,.10);
      }
      .v2-cta-section .v2-eyebrow { color: #15803d; }
      .v2-cta-section .v2-gift-label { color: #15803d; }
      .v2-pdf-btn {
        display: inline-flex; align-items: center; gap: 9px; margin-top: 4px;
        background: #fff; border: 2px solid #15803d; color: #15803d !important;
        font-weight: 800; font-size: 14.5px; padding: 12px 20px; border-radius: 100px;
        text-decoration: none; transition: background-color .2s, color .2s, transform .2s;
      }
      .v2-pdf-btn:hover { background: #15803d; color: #fff !important; transform: translateY(-2px); }
      .v2-cta-title { font-size: 28px; font-weight: 800; line-height: 1.25; margin: 0 0 10px; max-width: 760px; }
      .v2-cta-sub { color: var(--ink); margin: 0 0 26px; font-size: 16px; font-weight: 600; }
      .v2-cta-gifts { display: flex; align-items: center; gap: 18px; margin: 24px 0; flex-wrap: wrap; }
      .v2-cta-gifts img {
        height: 260px; border-radius: 8px; box-shadow: 0 14px 34px rgba(15,23,42,.24);
        transition: transform .25s;
      }
      .v2-cta-gifts img:hover { transform: translateY(-4px) rotate(-1deg); }
      .v2-gift-text { flex: 1 1 220px; }
      .v2-gift-label {
        text-transform: uppercase; letter-spacing: .12em; color: var(--mv);
        font-size: 10.5px; font-weight: 800;
      }
      .v2-gift-text p { margin: 5px 0 0; font-size: 15px; color: var(--ink); }
      .v2-calendar-card {
        background: #fff; border: 1px solid var(--bd); border-radius: 16px;
        padding: 24px; margin: 22px 0;
        display: flex; flex-direction: column; align-items: flex-start; gap: 14px;
      }
      .v2-calendar-header { display: flex; align-items: center; gap: 9px; font-size: 18px; font-weight: 800; }
      .v2-calendar-slots { display: flex; gap: 8px; font-size: 12.5px; flex-wrap: wrap; }
      .v2-calendar-slots div {
        padding: 6px 13px; background: #f1f5f9; border: 1px solid var(--bd);
        border-radius: 100px; color: var(--ink); font-weight: 600; font-size: 13px;
      }
      .v2-cta-alt { font-size: 14.5px; color: var(--ink); }
      .v2-cta-alt a { color: var(--mv); font-weight: 700; }
      .v2-creds {
        margin-top: 26px; padding-top: 24px; border-top: 1px solid #bbf7d0;
      }
      .v2-creds-head { font-size: 22px; font-weight: 800; line-height: 1.25; }
      .v2-creds-sub { font-size: 14.5px; color: var(--ink); margin-top: 4px; }
      .v2-creds-title {
        margin-top: 20px; font-size: 10.5px; font-weight: 800; letter-spacing: .14em;
        text-transform: uppercase; color: #15803d;
      }
      .v2-creds-list { list-style: none; padding: 0; margin: 10px 0 0; }
      .v2-creds-list li {
        display: flex; align-items: flex-start; gap: 8px; padding: 6px 0;
        font-size: 14px; color: var(--ink); line-height: 1.45;
      }
      .v2-creds-list svg { color: #15803d; flex-shrink: 0; margin-top: 2px; }
      .v2-reviews { margin-top: 20px; display: flex; flex-direction: column; gap: 12px; }
      .v2-reviews blockquote {
        background: #fff; border: 1px solid var(--bd); padding: 16px; border-radius: 12px;
        margin: 0; font-style: italic; color: var(--ink2); font-size: 14.5px;
      }
      .v2-reviews cite { display: block; margin-top: 8px; font-size: 13px; font-style: normal; color: var(--ink3); }

      /* ---------- tech */
      .v2-tech { max-width: 1120px; margin: 24px auto; padding: 0 24px; }
      .v2-tech-link {
        display: inline-flex; align-items: center; gap: 9px; cursor: pointer;
        background: #fff; border: 2px solid var(--mv); color: var(--mv);
        font-family: inherit; font-weight: 800; font-size: 15.5px;
        padding: 14px 22px; border-radius: 100px; text-align: left;
        transition: background-color .2s, color .2s, transform .2s;
      }
      .v2-tech-link:hover { background: var(--mv); color: #fff; transform: translateY(-2px); }
      .v2-tech-body {
        background: #fff; padding: 32px 34px; border-radius: 16px; border: 1px solid var(--bd);
        margin-top: 16px;
      }
      .v2-tblock { padding: 26px 0; border-top: 1px solid #eef2f7; }
      .v2-tblock:first-child { border-top: none; padding-top: 0; }
      .v2-tblock h3 { font-size: 16px; font-weight: 800; margin: 0 0 16px; }
      .v2-tgrid2 { display: grid; grid-template-columns: repeat(auto-fit, minmax(330px, 1fr)); gap: 4px 36px; }
      .v2-trow {
        display: flex; justify-content: space-between; gap: 18px; align-items: baseline;
        padding: 11px 0; border-bottom: 1px solid #f1f5f9; font-size: 14px; line-height: 1.5;
      }
      .v2-trow-l { color: var(--ink2); min-width: 0; }
      .v2-trow-v { font-weight: 800; text-align: right; overflow-wrap: anywhere; }
      .v2-tlist { list-style: none; padding: 0; margin: 0; }
      .v2-tlist li {
        padding: 11px 0; border-bottom: 1px dotted #e8eef5; font-size: 13.5px; color: var(--ink);
      }
      .v2-tlist code { background: #f1f5f9; padding: 2px 6px; border-radius: 4px; font-size: 11.5px; }
      .v2-tdesc { color: var(--ink3); font-size: 12.5px; margin-top: 4px; line-height: 1.45; }
      .v2-tpill {
        display: inline-block; background: var(--mv-bg); color: var(--mv); border: 1px solid var(--mv-bd);
        font-size: 10px; font-weight: 800; text-transform: uppercase; letter-spacing: .06em;
        padding: 2px 8px; border-radius: 100px; margin-right: 8px;
      }
      .v2-ttable-wrap { overflow-x: auto; -webkit-overflow-scrolling: touch; }
      .v2-ttable { width: 100%; border-collapse: collapse; font-size: 13px; min-width: 520px; }
      .v2-ttable th {
        text-align: left; font-size: 10.5px; text-transform: uppercase; letter-spacing: .08em;
        color: var(--ink3); border-bottom: 1px solid var(--bd); padding: 8px 10px 8px 0;
      }
      .v2-ttable td { padding: 8px 10px 8px 0; border-bottom: 1px dotted #e8eef5; }
      .v2-turl { max-width: 360px; overflow-wrap: anywhere; color: var(--ink2); }
      .v2-sev-badge {
        display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 9.5px;
        font-weight: 800; text-transform: uppercase; margin-right: 6px;
      }
      .v2-sev-critical { background: #fef2f2; color: #b91c1c; }
      .v2-sev-important { background: #fefce8; color: #a16207; }
      .v2-sev-minor { background: #f1f5f9; color: #475569; }

      /* ---------- footer + sticky */
      .v2-footer {
        text-align: center; padding: 30px 20px; color: var(--ink3); font-size: 12px;
        border-top: 1px solid var(--bd); margin-top: 36px;
      }
      .v2-footer a { color: var(--mv); }
      .v2-sticky-cta { display: none; }

      /* ---------- responsive */
      @media (max-width: 1023px) {
        .v2-diag-grid { grid-template-columns: minmax(0, 1fr); gap: 26px; }
        .v2-nums-col { position: static; }
      }
      @media (max-width: 640px) {
        .v2-header-in { padding: 12px 16px; gap: 10px; }
        .v2-logo { height: 54px; }
        .v2-phone-pill { padding: 6px 10px; font-size: 12px; }
        .v2-hero { padding: 30px 16px 26px; }
        .v2-title { font-size: 27px; }
        .v2-title-sector { font-size: 16px; }
        .v2-lede { font-size: 15.5px; }
        .v2-cta { width: 100%; justify-content: center; padding: 15px 18px; font-size: 15px; }
        .v2-cta-sm { width: auto; }
        .v2-h2 { font-size: 21px; }
        .v2-diag { padding: 0 16px 16px; }
        .v2-step { grid-template-columns: 38px minmax(0, 1fr); gap: 12px; }
        .v2-step-dot { width: 36px; height: 36px; }
        .v2-step-dot svg { width: 18px; height: 18px; }
        .v2-step-body { padding: 14px; }
        .v2-step-break .v2-step-body { padding-top: 24px; }
        .v2-break-badge { font-size: 9px; padding: 4px 9px; left: 10px; }
        .v2-step-title { font-size: 15px; }
        .v2-goal-title { font-size: 18px; }
        .v2-sector, .v2-card-sec, .v2-cost, .v2-cta-section { padding: 22px 16px; border-radius: 14px; margin: 16px 12px; }
        .v2-tech { padding: 0 16px; }
        .v2-tech-link { font-size: 14px; padding: 13px 16px; width: 100%; }
        .v2-tech-body { padding: 16px; }
        .v2-tgrid2 { grid-template-columns: minmax(0, 1fr); }
        .v2-trow {
          flex-direction: column; align-items: flex-start; gap: 3px;
          font-size: 13.5px; padding: 12px 0;
        }
        .v2-trow-v { text-align: left; }
        .v2-tech-body { padding: 20px 18px; }
        .v2-tblock { padding: 22px 0; }
        .v2-sector-big { font-size: 19px; padding: 12px 14px; }
        .v2-kw-head, .v2-kw-row { grid-template-columns: minmax(0,1fr) 42px 82px; gap: 6px; }
        .v2-kw-head { font-size: 9px; letter-spacing: .04em; }
        .v2-kw-name { font-size: 13.5px; }
        .v2-cta-title { font-size: 21px; }
        .v2-bar-line { grid-template-columns: minmax(0, 1fr) 72px; gap: 8px 10px; }
        .v2-bar-name { grid-column: 1 / -1; }
        .v2-bar-track { height: 18px; }
        .v2-kw-callout { font-size: 13.5px; padding: 14px; }
        .v2-creds-head { font-size: 18px; }
        .v2-pdf-btn { width: 100%; justify-content: center; }
        .v2-cta-gifts img { height: 170px; }
        .v2-calendar-card { padding: 18px; }
        .v2-cost-line { font-size: 16.5px; }
        .v2-sticky-cta {
          display: flex; align-items: center; justify-content: space-between; gap: 10px;
          position: fixed; bottom: 0; left: 0; right: 0; z-index: 100;
          background: rgba(255,255,255,.96); backdrop-filter: blur(10px);
          border-top: 1px solid var(--bd); padding: 10px 14px;
          text-decoration: none; color: var(--ink) !important;
          box-shadow: 0 -6px 20px rgba(15,23,42,.08);
        }
        .v2-sticky-cta span:first-child { font-size: 13px; font-weight: 700; }
        .v2-sticky-cta em { color: var(--mv); font-style: normal; font-weight: 800; }
        .v2-sticky-btn {
          background: var(--mv); color: #fff; font-weight: 800; font-size: 14px;
          padding: 11px 18px; border-radius: 100px; white-space: nowrap;
        }
      }
    `}</style>
  );
}
